/**
 * GA4 Analytics Events
 *
 * This module provides GA4-specific event tracking via gtag.
 * Existing analytics.js (Segment) remains unchanged.
 */

// GA4 Event Names
export const GA4Events = {
  PHONE_CLICK: 'phone_click',
  QUESTIONNAIRE_SUBMIT: 'questionnaire_submit',
  SIGNUP_FORM_SUBMIT: 'signup_form_submit',
  INQUIRE_CLICK: 'inquire_click',
  SCHEDULE_TOUR_CLICK: 'schedule_tour_click',
  SAVE_LISTING: 'save_listing',
  ACCOUNT_CREATE: 'account_create',
  CHAT_START: 'chat_start',
};

/**
 * Send event to GA4 via gtag
 * @param {string} eventName - Event name from GA4Events
 * @param {Object} params - Event parameters
 */
export function trackGA4(eventName, params = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

/**
 * Track phone click event
 * @param {string} phoneNumber - The phone number clicked
 */
export function trackPhoneClick(phoneNumber) {
  trackGA4(GA4Events.PHONE_CLICK, {
    phone_number: phoneNumber,
  });
}

/**
 * Track questionnaire submission (onboarding final step)
 * @param {Object} params - Questionnaire data
 * @param {string|number} params.employees - Number of employees
 * @param {string|number} params.space - Space requirements
 * @param {string} params.location - Selected location
 * @param {string|number} params.budget - Budget
 */
export function trackQuestionnaireSubmit({ employees, space, location, budget }) {
  trackGA4(GA4Events.QUESTIONNAIRE_SUBMIT, {
    employees,
    space,
    location,
    budget,
  });
}

/**
 * Track signup form submission on listing pages
 * @param {Object} params - Form data
 * @param {string} params.pageType - "listing_index" or "building_detail"
 * @param {string} [params.location] - Location
 * @param {string|number} [params.listingId] - Listing ID (if on building detail)
 */
export function trackSignupFormSubmit({ pageType, location, listingId }) {
  trackGA4(GA4Events.SIGNUP_FORM_SUBMIT, {
    page_type: pageType,
    location,
    ...(listingId && { listing_id: listingId }),
  });
}

/**
 * Track inquire button click on building page
 * @param {Object} params
 * @param {string|number} params.listingId - Listing ID
 * @param {string} params.buildingName - Building name
 * @param {string} params.city - City
 */
export function trackInquireClick({ listingId, buildingName, city }) {
  trackGA4(GA4Events.INQUIRE_CLICK, {
    listing_id: listingId,
    building_name: buildingName,
    city,
  });
}

/**
 * Track schedule tour button click on building page
 * @param {Object} params
 * @param {string|number} params.listingId - Listing ID
 * @param {string} params.buildingName - Building name
 * @param {string} params.city - City
 */
export function trackScheduleTourClick({ listingId, buildingName, city }) {
  trackGA4(GA4Events.SCHEDULE_TOUR_CLICK, {
    listing_id: listingId,
    building_name: buildingName,
    city,
  });
}

/**
 * Track save listing button click on building page
 * @param {Object} params
 * @param {string|number} params.listingId - Listing ID
 * @param {string} params.buildingName - Building name
 * @param {string} params.city - City
 */
export function trackSaveListing({ listingId, buildingName, city }) {
  trackGA4(GA4Events.SAVE_LISTING, {
    listing_id: listingId,
    building_name: buildingName,
    city,
  });
}

/**
 * Track successful account creation
 * @param {Object} params
 * @param {string} params.sourcePage - Page where account was created
 * @param {string|number} [params.listingId] - Listing ID if present
 * @param {string} [params.city] - City
 */
export function trackAccountCreate({ sourcePage, listingId, city }) {
  trackGA4(GA4Events.ACCOUNT_CREATE, {
    source_page: sourcePage,
    ...(listingId && { listing_id: listingId }),
    ...(city && { city }),
  });
}

/**
 * Track chat start with SquareFoot specialist
 * @param {Object} params
 * @param {string|number} params.listingId - Listing ID
 * @param {string} params.pageUrl - Current page URL
 * @param {string} params.buildingName - Building name
 * @param {string} params.city - City
 */
export function trackChatStart({ listingId, pageUrl, buildingName, city }) {
  trackGA4(GA4Events.CHAT_START, {
    listing_id: listingId,
    page_url: pageUrl,
    building_name: buildingName,
    city,
  });
}
